<template>
    <div :class="$style.experienceDialogWrap">
        <pt-dialog
            v-model="props.visible"
            :custom-class="[
                $style.templateDetailDialog,
                isMobile && $style.templateDetailDialogMobile
            ]"
            :show-close="isMobile"
            top="20px"
            :fullscreen="isMobile"
            :center="true"
            @close="handleClose"
        >
            <teleport :to="`.${$style.templateDetailDialog}`" v-if="!isMobile">
                <pt-icon
                    icon="pt-icon-solid-close"
                    class="pt-dialog__outside-close"
                    @click="handleClose"
                ></pt-icon>
            </teleport>
            <div :class="$style.content">
                <wac-component
                    v-if="currentShowTemplate?.isfromEC"
                    display-mode="detail"
                    :lang="wacLang"
                    :component-data="currentShowTemplate?.data"
                    :asset-id="currentShowTemplate?.id"
                    @view="handleUseTemplate"
                ></wac-component>
                <TemplateDetail
                    v-else
                    :template="currentShowTemplate"
                    @use="handleUseTemplate"
                ></TemplateDetail>
            </div>
            <TemplateRecommandList
                position="template_detailpage_recommend"
                :entryPosition="props.entryPosition"
                :class="$style.moreList"
                :isFromEC="props.template?.isfromEC"
                :currentTemplate="currentShowTemplate"
                :isMobile="isMobile"
                recommandType="purpose"
                @view="handleSelectMoreTemplate"
                @create="handleUseTemplate"
            >
                <template #left>
                    <p :class="$style.moreTitle">
                        {{
                            currentShowTemplate?.isfromEC
                                ? $t('experience.onboarding.more_ideas_like')
                                : $t('experience.onboarding.more_template_like')
                        }}
                    </p>
                </template>
            </TemplateRecommandList>
        </pt-dialog>
    </div>
</template>

<script lang="ts">
import TemplateDetail from '@experience/components/template/TemplateDetail.vue';
import TemplateRecommandList from '@experience/components/template/TemplateRecommandList.vue';
export default {
    name: 'ExperienceTemplateDetail',
    components: {
        TemplateDetail,
        TemplateRecommandList
    }
};
</script>

<script lang="ts" setup>
import { ref, watch, onMounted, getCurrentInstance } from 'vue';
import type { ExperienceTemplateV4 } from '@models/experience/useExperienceTemplatesV4';
import type { ECTemplate } from '@models/ecshop/asset';
import { useECTemplates } from '@models/ecshop/asset';
import analyticsService from '@assets/services/analytics.service';
import { useRoleDialog } from '@master/hooks/service/useShowAuthDialog';
import { useRouter, useRoute } from 'vue-router';
import { useECEvent, ECDataLocation } from '@experience/hooks/template.hooks';

// interface
interface Props {
    visible: boolean;
    template: ExperienceTemplateV4 | ECTemplate;
    changeRoute: boolean;
    position: ECDataLocation;
    entryPosition?: string;
}
// props
const props = withDefaults(defineProps<Props>(), {
    changeRoute: false
});
const isMobile = ref(window.screen.width <= 750);

// emits
const emit = defineEmits(['close', 'create']);
const proxy = getCurrentInstance()?.proxy;
const router = useRouter();
const route = useRoute();

// state
const currentShowTemplate = ref<ExperienceTemplateV4 | ECTemplate | null>(props.template);
const { wacLang } = useECTemplates();
// computed

// watch
watch(
    () => props.template,
    () => {
        currentShowTemplate.value = props.template;
    }
);

onMounted(() => {
    // useECEvent('trackProductDetailImpression', props.template, props.position);
    // analyticsService
    //     .usePTX('cp_template_detail_click', {
    //         position: props.position,
    //         template_name: props.template.name
    //     })
    //     .track();
});

// onUnmounted

// function
function handleClose() {
    emit('close');
}
function handleSelectMoreTemplate(template: ExperienceTemplateV4 | ECTemplate) {
    if (props.changeRoute) {
        template?.id &&
            router.push({
                name: route.name === 'ProductGalleryView' ? 'ProductGalleryView' : 'GalleryView',
                params: {
                    templateId: template?.id
                },
                query: route.query
            });
    }

    currentShowTemplate.value = template;

    setTimeout(() => {
        // @ts-ignore
        const scrollEle = document.body.querySelector(`.${proxy.$style.templateDetailDialog}`);

        scrollEle?.querySelector('.pt-dialog__body')?.scrollTo({
            left: 0,
            top: 0
        });
    }, 0);
}
function handleUseTemplate() {
    if (!useRoleDialog() || !currentShowTemplate.value) return;

    emit('create', currentShowTemplate.value);
    useECEvent('trackAddToCart', currentShowTemplate.value, props.position);
    analyticsService
        .usePTX('cp_template_create_click', {
            type: currentShowTemplate.value.type,
            is_blank: 'N',
            template_position: 'template_detail',
            template_name: currentShowTemplate.value.name
        })
        .track();
}
</script>

<style lang="scss" module>
@import '@style/import.scss';
.experienceDialogWrap {
    position: relative;
    z-index: calc(#{$page-help-index} + 1); // 需要比start guide层级高
}
.templateDetailDialog {
    min-width: calc(100% - 90px);
    max-height: calc(100% - 40px);
    box-sizing: border-box;
    display: flex;
    position: relative;
    flex-direction: column;
    justify-content: center;
    align-items: center;
    margin-bottom: 20px !important;

    :global {
        .pt-dialog__body {
            width: 100%;
            flex: 1;
            max-height: 100%;
            padding-right: 0 !important;
            padding-left: 0 !important;
            overflow-y: auto;
            position: relative;
            box-sizing: border-box;
            padding-bottom: 0 !important;
            padding-top: 0 !important;
            border-radius: 10px;

            .ec-card-container {
                padding-bottom: 20px;
            }
        }

        .pt-dialog__outside-close {
            position: absolute;
            width: 28px;
            height: 28px;
            fill: #b3bac5;
            right: -36px;
            top: -8px;
            cursor: pointer;
            transition: 0.15s linear;

            &:hover {
                fill: #dfe1e6;
            }
        }
    }

    .content {
        min-height: 400px;
        box-sizing: border-box;
        background-color: #ffffff;
        padding: 40px;
        max-width: 980px;
        display: flex;
        justify-content: center;
        box-sizing: border-box;
        margin: 0 auto;

        & > div {
            width: 100%;
        }
    }
    .moreList {
        .moreTitle {
            font-size: 20px;
            color: $pt-black-900;
            margin-bottom: 20px;
        }
    }
}

.templateDetailDialogMobile {
    width: 100% !important;
    max-height: 100% !important;
    margin-top: 0 !important;
    margin-bottom: 0 !important;

    :global {
        .pt-dialog__body {
            height: 100%;
        }
    }

    .content {
        padding: 0;
    }
}
</style>
